/* Copyright (c) 2022-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { fromEvent, Observable } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

export type KeyboardEventBackKey = 'Escape' | 'GoBack';

export type KeyboardEventArrowKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

export enum KeyboardEventBackKeyValue {
    Escape = 'Escape',
    GoBack = 'GoBack'
}

export enum KeyboardEventArrowKeyValue {
    ArrowUp = 'ArrowUp',
    ArrowDown = 'ArrowDown',
    ArrowLeft = 'ArrowLeft',
    ArrowRight = 'ArrowRight'
}

export type KeyboardEventKey = KeyboardEventArrowKey | 'Enter' | KeyboardEventBackKey;

export type KeyboardEventType = 'keydown' | 'keyup';

export function isBackKey(event: KeyboardEvent) {
    return event.key === 'Escape' || event.key === 'GoBack';
}

export function isArrowKey(event: KeyboardEvent): boolean {
    return (
        event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight'
    );
}

export function fromKeyboardEvent(
    target: EventTarget,
    eventType: KeyboardEventType,
    key?: KeyboardEventKey
): Observable<KeyboardEvent> {
    const event$ = fromEvent<KeyboardEvent>(target, eventType);
    return key ? event$.pipe(filter(event => event.key === key)) : event$;
}

/**
 * The method returns an observable that consumes keydown events of a given key until a user has released the given key
 *
 * @param target the event target that the listener is installed to
 * @param key the key that you want to consume
 */
export function consumeKeydownUntilKeyup(target: EventTarget, key: KeyboardEventKey): Observable<KeyboardEvent> {
    const keyup = fromEvent(target, 'keyup', { capture: true }).pipe(
        filter((e: KeyboardEvent) => e.key === key),
        take(1)
    );
    return fromEvent(target, 'keydown', { capture: true }).pipe(
        filter((e: KeyboardEvent) => e.key === key),
        tap(event => {
            event.stopPropagation();
            event.preventDefault();
        }),
        takeUntil(keyup)
    );
}
